import { Suspense } from 'react';
import { useTracking } from 'react-tracking';

import { NarrativeFairValueActionSheet } from './components/NarrativeFairValueActionSheet';
import { ValuationSelectionForm } from './components/ValuationSelectionForm';
import { Alert, AlertAction, Button } from '@simplywallst/ui-core';
import { ErrorBoundary } from '@sentry/react';
import { NarrativeFairValueHeader } from './components/NarrativeFairValueHeader';
import { NarrativeCardLoading } from '@/components/NarrativeCard';

export interface Props {
  companyId: string;
  isModalOpen: boolean;
  onClose: () => void;
  userHoldsPosition?: boolean;
}

export const NarrativeFairValueModal = ({
  companyId,
  isModalOpen,
  onClose,
  userHoldsPosition,
}: Props) => {
  const { Track } = useTracking({
    type: 'track',
    subject: 'FairValue',
    companyId,
  });

  return (
    <Track>
      <NarrativeFairValueActionSheet isOpen={isModalOpen} onClose={onClose}>
        <ErrorBoundary
          fallback={
            <div className="grid grid-rows-[max-content_max-content] gap-4">
              <NarrativeFairValueHeader>Valuation</NarrativeFairValueHeader>
              <div className="px-6">
                <Alert
                  styleType="negative"
                  actions={
                    <AlertAction onClick={() => window.location.reload()}>
                      Reload
                    </AlertAction>
                  }
                >
                  Failed to load narratives and fair value. Please try again.
                </Alert>
              </div>
            </div>
          }
        >
          <Suspense
            fallback={
              <div
                className="grid h-full grid-rows-[max-content_1fr_max-content] overflow-hidden"
                role="presentation"
                aria-label="Loading skeleton"
              >
                <NarrativeFairValueHeader>Valuation</NarrativeFairValueHeader>
                <div className="scrollbar relative h-full overflow-y-auto overflow-x-hidden">
                  <div className="grid grid-cols-1 grid-rows-[max-content_max-content] gap-2 py-6 min-[375px]:px-x2 sm:grid-cols-2 lg:px-x4">
                    <NarrativeCardLoading />
                    <NarrativeCardLoading />
                  </div>
                </div>
                <div className="grid grid-cols-1 justify-items-end bg-surface py-4 pr-x2 lg:px-x4">
                  <Button
                    styleType="louderLarge"
                    onClick={onClose}
                    data-cy-id="narrative-fair-value-modal-done"
                  >
                    Done
                  </Button>
                </div>
              </div>
            }
          >
            {isModalOpen && (
              <ValuationSelectionForm
                onClose={onClose}
                companyId={companyId}
                userHoldsPosition={userHoldsPosition}
              />
            )}
          </Suspense>
        </ErrorBoundary>
      </NarrativeFairValueActionSheet>
    </Track>
  );
};
